import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useContracts } from '../api/hooks';
import PageHeader from '../components/PageHeader';
import AgentChip from '../components/AgentChip';
import { Button } from '../components/ui/button';
import StatusPill from '../components/StatusPill';
import { Upload, FileText, Eye } from 'lucide-react';
import { toast } from '../lib/aiToast';

export default function Contracts() {
  const { session } = useAuth();
  const practiceId = session?.practice?.id || 'prac_001';
  const { data: serverContracts = [] } = useContracts(practiceId);
  // Server-backed list, with locally-uploaded contracts layered on top so the
  // demo OCR/parse simulation keeps working without a real upload endpoint.
  const [uploaded, setUploaded] = useState([]);
  const [uploading, setUploading] = useState(false);
  const contracts = [...uploaded, ...serverContracts];

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setTimeout(() => {
      const next = { id: 'ct_' + Date.now(), practiceId, payerName: 'New payer (set after parse)', fileName: file.name, uploadedBy: session?.user?.id, uploadedAt: '2026-05-04', ocrStatus: 'in_progress', parseStatus: 'queued', effectiveDate: null, termDate: null, includedPlans: [], lineCount: 0 };
      setUploaded((prev) => [next, ...prev]);
      setUploading(false);
      toast.success('Uploaded — Contract Agent running OCR');
      setTimeout(() => {
        setUploaded((prev) => prev.map((c) => c.id === next.id ? { ...c, ocrStatus: 'complete', parseStatus: 'pending_review', lineCount: 124, effectiveDate: '2026-01-01', termDate: '2027-12-31', includedPlans: ['Newly extracted plan'] } : c));
        toast.success('Parsed — 124 lines ready for review');
      }, 3200);
    }, 1000);
  };

  const parseLabel = (s) => (s === 'reviewed' ? 'done' : s === 'pending_review' ? 'awaiting_review' : s === 'queued' ? 'queued' : 'running');

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Directory" title="Contracts" subtitle="The Contract Agent OCRs payer PDFs and extracts dates, fee schedules, and plans — you confirm before they go live.">
        <label className="cursor-pointer">
          <input type="file" accept="application/pdf" className="hidden" onChange={onUpload} disabled={uploading} />
          <Button asChild disabled={uploading} className="bg-brand-600 hover:bg-brand-700 text-white">
            <span><Upload className="h-4 w-4 mr-1.5" />{uploading ? 'Uploading…' : 'Upload contract'}</span>
          </Button>
        </label>
      </PageHeader>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {contracts.map((c) => (
          <div key={c.id} className="dh-card p-5 lift">
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 rounded-lg bg-brand-50 flex items-center justify-center"><FileText className="h-5 w-5 text-brand-600" /></div>
              <StatusPill status={parseLabel(c.parseStatus)} label={c.parseStatus === 'pending_review' ? 'Needs review' : c.parseStatus === 'reviewed' ? 'Live' : undefined} />
            </div>
            <div className="mt-4 font-semibold text-slate-900 truncate">{c.payerName}</div>
            <div className="text-xs text-slate-500 truncate mono">{c.fileName}</div>
            <dl className="mt-3 space-y-1.5 text-xs">
              <div className="flex justify-between"><dt className="text-slate-500">Effective</dt><dd className="text-slate-900 tnum">{c.effectiveDate || 'pending'}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Term</dt><dd className="text-slate-900 tnum">{c.termDate || '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Plans</dt><dd className="text-slate-900">{c.includedPlans.length} extracted</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Fee lines</dt><dd className="text-slate-900 tnum">{c.lineCount}</dd></div>
            </dl>
            <div className="mt-3 flex items-center justify-between">
              <AgentChip agent="verification" label="Contract" size="xs" />
              <Link to={`/app/contracts/${c.id}`}><Button size="sm" variant="outline" className="h-7"><Eye className="h-3.5 w-3.5 mr-1.5" />Review</Button></Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
